import React, { FC, memo } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import Link from 'next/link'
import { Grid, GridCol } from '@ucheba/ui/components/Grid/desktop'
import { Text } from '@ucheba/ui/components/Text/desktop'
import { Chips } from '@ucheba/ui/components/Chips/desktop'
import { Spacing } from '@ucheba/ui/components/Spacing/desktop'
import { spaceDigits } from '@ucheba/utils/helpers/number'
import StarIcon from '@ucheba/ui/icons/icon__star-filled.svg'
import { plannerProgramsSelectors } from '@ucheba/store/planner/programs'
import { useProbability, useProgramListItemCore } from '../bll'
import SC from '../base/ProgramsItem.styles'
import { ETextColorCheap, IProgramsItemProps } from '../types'
import { useWantToStudy } from '../../LayoutInstitution/components/WantToStudy/bll/useWantToStudy'
import WantToStudy from '../../LayoutInstitution/components/WantToStudy/desktop'

const { Block } = SC

const Image = styled.img`
  width: 16px;
`

const ProgramsItem: FC<IProgramsItemProps> = (props) => {
  const {
    isDivider = true,
    id,
    name,
    programType,
    faculty,
    budgetPlacesCount,
    optionalSubjects,
    requiredSubjects,
    priceFrom,
    institution,
    feedbackStats,
    probability,
    additionalExams,
    averageBySubjectExamScoreFrom,
    linkTarget = '_blank',
  } = props

  const { icons } = useProgramListItemCore()

  const programIds = useSelector(plannerProgramsSelectors.entityProgramsIds)

  const probabilityData = useProbability(probability)

  const { isInPlanOfAdmission, handleWantToStudy } = useWantToStudy({
    id,
    ids: programIds?.entityIds || [],
    type: 'program',
  })

  return (
    <Block isDivider={isDivider}>
      <Grid nowrap justifyContent='space-between'>
        <GridCol width='auto' shrink='initial'>
          {faculty?.id && faculty?.abbreviation ? (
            <>
              <Text color='blue' html={false}>
                <Link href={`/uz/${faculty?.id}`} target={linkTarget}>
                  {faculty?.abbreviation}
                </Link>
              </Text>

              <Spacing spacing='xxsmall' />
            </>
          ) : (
            <>
              <Text color='blue' html={false}>
                <Link href={`/uz/${institution?.id}`} target={linkTarget}>
                  {institution?.name}
                </Link>
              </Text>

              <Spacing spacing='xxsmall' />
            </>
          )}

          <Text fontSize='h3' fontWeight='bold' lineHeight='h3' color='blue' html={false}>
            <a
              href={`/program/${id}`}
              color='inherit'
              target={linkTarget}
              rel='noreferrer'
            >
              {name.replace(/&quot;/g, '"')}
            </a>
          </Text>

          <Spacing spacing='xxsmall' />

          <Grid nowrap justifyContent='flex-start' spacing='xsmall'>
            {feedbackStats?.score && (
              <GridCol width='auto'>
                <Text
                  html={false}
                  color='orange'
                  fontWeight='semibold'
                  lineHeight='small2'
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ display: 'inline-block' }}>
                      <StarIcon width='20px' height='20px' color='#FF6A00' />
                    </span>
                    <span style={{ display: 'inline-block', paddingLeft: '4px' }}>
                      {feedbackStats.score}
                    </span>
                  </div>
                </Text>
              </GridCol>
            )}

            {averageBySubjectExamScoreFrom && (
              <GridCol width='auto'>
                <Text html={false} fontWeight='regular' color='gray60'>
                  <span style={{ fontWeight: '600', color: 'black' }}>
                    {Math.floor(averageBySubjectExamScoreFrom)}
                  </span>{' '}
                  <span>проходной балл</span>
                </Text>
              </GridCol>
            )}

            <GridCol width='auto'>
              <Text html={false} fontWeight='regular' color='gray60'>
                <span style={{ fontWeight: '600', color: 'black' }}>
                  {budgetPlacesCount || 0}
                </span>{' '}
                <span>бюджетных мест</span>
              </Text>
            </GridCol>

            {priceFrom && (
              <GridCol width='auto'>
                <Text html={false} fontWeight='regular' color='gray60'>
                  <span style={{ fontWeight: '600', color: 'black' }}>
                    {`от ${spaceDigits(priceFrom)} р.`}
                  </span>{' '}
                  <span>за год</span>
                </Text>
              </GridCol>
            )}
          </Grid>

          <Spacing spacing='xxsmall' />

          {requiredSubjects && requiredSubjects.length > 0 && (
            <Grid spacing='xsmall'>
              <GridCol width='auto'>
                <Text fontSize='small2' fontWeight='semibold'>
                  Обязательно:
                </Text>
              </GridCol>

              {requiredSubjects.map((item) => {
                const Icon = item.slug && icons[item.slug]

                return (
                  <GridCol key={item.id} width='auto'>
                    <Grid spacing='uxsmall' alignItems='center'>
                      {Icon && <Icon width='18px' height='18px' color='black' />}

                      <Text
                        as='span'
                        fontSize='small1'
                        lineHeight='small1'
                        firstLetterUppercase
                        color='black'
                      >
                        {item.name}
                      </Text>
                    </Grid>
                  </GridCol>
                )
              })}
            </Grid>
          )}

          {optionalSubjects && optionalSubjects.length > 0 && (
            <Grid spacing='xsmall'>
              <GridCol width='auto'>
                <Text fontSize='small2' fontWeight='semibold'>
                  По выбору:
                </Text>
              </GridCol>

              {optionalSubjects.map((el) => {
                const Icon = el.slug && icons[el.slug]

                return (
                  <GridCol key={el.id} width='auto'>
                    <Grid spacing='uxsmall' alignItems='center'>
                      {Icon && <Icon width='18px' height='18px' color='black' />}

                      <Text
                        as='span'
                        fontSize='small1'
                        lineHeight='small1'
                        firstLetterUppercase
                        color='black'
                      >
                        {el.name}
                      </Text>
                    </Grid>
                  </GridCol>
                )
              })}
            </Grid>
          )}

          {additionalExams && additionalExams.length > 0 && (
            <Grid spacing='xsmall'>
              <GridCol width='auto'>
                <Text fontSize='small2' fontWeight='semibold'>
                  Доп. экзамены:
                </Text>
              </GridCol>

              {additionalExams &&
                additionalExams.map((exam) => {
                  return (
                    <GridCol key={exam.id} width='auto'>
                      <Grid spacing='uxsmall' alignItems='center'>
                        {!!exam.subject && !!exam.subject.iconUrl && (
                          <GridCol width='18px'>
                            <Image src={`https://ucheba.ru${exam.subject.iconUrl}`} />
                          </GridCol>
                        )}

                        <GridCol width='auto'>
                          <Text
                            as='span'
                            fontSize='small1'
                            lineHeight='small1'
                            firstLetterUppercase
                            color='black'
                          >
                            {exam.title}
                          </Text>
                        </GridCol>
                      </Grid>
                    </GridCol>
                  )
                })}
            </Grid>
          )}

          {!requiredSubjects?.length &&
            !optionalSubjects?.length &&
            !additionalExams?.length && (
              <Grid spacing='xsmall'>
                <GridCol width='auto'>
                  <Text fontSize='small2' fontWeight='semibold'>
                    Без экзаменов
                  </Text>
                </GridCol>
              </Grid>
            )}

          <Spacing spacing='small' />

          <WantToStudy
            buttonText='Хочу поступить'
            block={false}
            isInPlanOfAdmission={isInPlanOfAdmission}
            handleWantToStudy={handleWantToStudy}
            size='small'
            type='program'
            programId={id}
            programType={programType.value}
            isShowingHint={false}
            view='outline'
            alternativeButtonView='outline'
          />
        </GridCol>

        <GridCol width='auto'>
          <Grid spacing='xxsmall'>
            <GridCol width='auto'>
              <Chips color='gray5' firstLetterUppercase>
                {programType.name}
              </Chips>
            </GridCol>

            <GridCol width='auto'>
              {probabilityData && (
                <Chips color={probabilityData?.color} firstLetterUppercase>
                  <Text color={ETextColorCheap[probabilityData.color]}>
                    {probabilityData?.txt}
                  </Text>
                </Chips>
              )}
            </GridCol>
          </Grid>
        </GridCol>
      </Grid>
    </Block>
  )
}

export { ProgramsItem }
export default memo(ProgramsItem)
